<template>
  <div>
    <div>
      <div class="overflow-x-auto overflow-y-auto mt-2" v-if="viewOrgTree">
        <TreeNode />
      </div>
      <div class="overflow-x-auto overflow-y-auto" v-else>
        <template v-if="!$vuetify.breakpoint.smAndDown">
          <template v-if="employees && employees.length > 0">
            <TableWrapper
              :headers="wrapperHeaders"
              :items="employees"
              :loading="isLoading"
              minWidth="1000px"
              class="mt-2"
            >
              <!-- Checkbox Header -->
              <template v-slot:[`header.select`]>
                <v-simple-checkbox
                  :value="isAllSelected"
                  @input="toggleSelectAll"
                  color="primary"
                  dense
                  hide-details
                />
              </template>

              <!-- Checkbox Item -->
              <template v-slot:[`item.select`]="{ item }">
                <v-simple-checkbox
                  :value="isRowSelected(item)"
                  @input="toggleRowSelected(item, $event)"
                  color="primary"
                  dense
                  hide-details
                />
              </template>

              <!-- Custom Headers -->
              <template v-for="(header, index) in tableHeaders" v-slot:[`header.${header.value}`]>
                <span
                  :key="'long-' + index"
                  class="font-title"
                  v-if="header.text.length > 15"
                  v-tooltip="{ text: header.text }"
                >
                  {{ header.text }}
                </span>
                <span v-else class="font-title inline-flex items-center" :key="'short-' + index">
                  {{ header.text }}
                  <v-icon
                    v-if="header.value === 'role'"
                    small
                    class="ml-1 text-gray-400 cursor-pointer"
                    v-tooltip="{
                      text: 'role is only for the access management of the system',
                      bottom: true
                    }"
                  >
                    mdi-information-outline
                  </v-icon>
                </span>
              </template>

              <!-- Custom Item Columns -->
              <template v-slot:[`item.firstName`]="{ item }">
                <span class="flex td_font">
                  <employee-info :showEmployeeId="true" :item="item" view="employeeTable" />
                </span>
              </template>

              <template v-slot:[`item.department`]="{ item }">
                <span class="font-medium">{{ item.department?.departmentName || '-' }}</span>
              </template>

              <template v-slot:[`item.designation`]="{ item }">
                <span class="font-medium">{{ item.designation?.designationName || '-' }}</span>
              </template>

              <template v-slot:[`item.branch`]="{ item }">
                <span class="font-medium">
                  {{ item.branch ? findBranch(item)?.branchName : '-' }}
                </span>
              </template>

              <template v-slot:[`item.team`]="{ item }">
                <span class="font-medium">
                  {{ item.team ? item.team?.teamName : '-' }}
                </span>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <span :class="getPartnerStatusClr(item.systemUserId?.status)">
                  {{ convertToTitleCase(item.systemUserId?.status) }}
                </span>
              </template>

              <template v-slot:[`item.role`]="{ item }">
                <v-autocomplete
                  hide-details
                  solo
                  :readonly="!hasIamPermission(iamStatements, 'organization.employees.update')"
                  flat
                  placeholder="No Role"
                  v-model="item.wfmRole"
                  :items="getRoleDropdown(item)"
                  item-text="role"
                  item-value="_id"
                  class="my-2 fixed-width-column"
                  :menu-props="{ offsetY: true }"
                  @change="updateRole(item)"
                  @click="newRoleId(item)"
                >
                  <template v-slot:selection="{ item }">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <div v-on="on" class="fixed-width-column">
                          {{ item.role }}
                        </div>
                      </template>
                      <span>{{ item.role }}</span>
                    </v-tooltip>
                  </template>
                </v-autocomplete>
              </template>

              <template v-slot:[`item.directManager`]="{ item }">
                <v-autocomplete
                  hide-details
                  solo
                  flat
                  placeholder="No Manager"
                  :readonly="!hasIamPermission(iamStatements, 'organization.employees.update')"
                  v-model="item.reportesTo"
                  :items="getSelectItems(item)"
                  :item-text="getManagerFullName"
                  item-value="_id"
                  @change="assignToManager(item)"
                  class="my-2 fixed-width-column"
                  :menu-props="{ offsetY: true }"
                >
                  <template v-slot:selection="{ item }">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <div v-on="on" class="fixed-width-column">
                          {{ getManagerFullName(item) }}
                        </div>
                      </template>
                      <span>{{ getManagerFullName(item) }}</span>
                    </v-tooltip>
                  </template>
                  <template v-slot:item="{ item: managerItem }">
                    <div v-if="managerItem._id !== null" class="d-flex align-center py-2">
                      <UserAvatar
                        :user="managerItem"
                        :size="24"
                        :show-name="true"
                        :name-class="'text-sm ml-2'"
                        :tooltip-enabled="false"
                      />
                    </div>
                    <div v-else class="d-flex align-center py-2">
                      <span class="ml-2 text-sm">Unassigned (No Role)</span>
                    </div>
                  </template>
                </v-autocomplete>
              </template>

              <template v-slot:[`item.action`]="{ item }">
                <action-button
                  :item="item"
                  :canEdit="hasIamPermission(iamStatements, 'organization.employees.update')"
                  :canDelete="false"
                  :showEditButton="hasIamPermission(iamStatements, 'organization.employees.update')"
                  :showDeleteButton="false"
                  :showLeaveButton="
                    hasIamPermission(iamStatements, 'organization.employees.update')
                  "
                  :showExpenseButton="
                    hasIamPermission(iamStatements, 'organization.employees.update')
                  "
                  :showHistoryButton="false"
                  :showDigitalProfileButton="
                    hasIamPermission(iamStatements, 'organization.employees.update')
                  "
                  :handleEdit="() => editUser(item)"
                  :handleLeaveAction="() => userLeaves(item)"
                  :handleExpenseAction="() => addUserExpense(item)"
                  :handleDelete="() => selectUserDelete(item)"
                  :handleHistoryAction="() => showEmployeeHistory(item)"
                  :handleDigitalProfileAction="() => showDigitalProfile(item)"
                  :handleLocationAction="() => openMap(item)"
                  :showLocationButton="isFeatureVisible('EMPLOYEE_LIVE_TRACKING')"
                  expenseIconText="Payroll"
                  expenseIcon="mdi-cash-multiple"
                />
              </template>
            </TableWrapper>

            <!-- Pagination Footer -->
            <div
              class="d-flex align-center justify-space-between mt-4 px-4 pb-4"
              v-if="totalPages > 1"
            >
              <span class="text-xs text-gray-500">
                Showing Page {{ options.page }} of {{ totalPages }} (Total: {{ totalEmployees }})
              </span>
              <div class="d-flex align-center gap-4">
                <span class="text-xs text-gray-500 mr-2">Rows per page:</span>
                <v-select
                  :items="[5, 9, 10, 15, 30]"
                  v-model="options.itemsPerPage"
                  hide-details
                  dense
                  outlined
                  class="text-xs mr-4"
                  style="width: 80px"
                ></v-select>
                <v-pagination
                  v-model="options.page"
                  :length="totalPages"
                  :total-visible="7"
                  circle
                  dense
                ></v-pagination>
              </div>
            </div>
          </template>
          <no-data title="Nothing to Display" subTitle="You need to import the employees" v-else>
            <v-btn
              @click="importEmployees"
              :color="$vuetify.theme.currentTheme.primary"
              :loading="loadingImport"
              :disabled="loadingImport"
              class="white--text"
              v-show="hasIamPermission(iamStatements, 'organization.employees.create')"
            >
              Import Employees
              <v-icon right dark> mdi-download </v-icon>
            </v-btn>
          </no-data>
        </template>
        <EmployeesPage_mobile v-else />
      </div>

      <!-- Updated Edit Employee Dialog to use EmployeeAdd component -->
      <v-dialog
        v-model="openEditEmployee"
        max-width="900px"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        persistent
      >
        <EmployeeAdd
          :key="editDialogKey"
          :disabled="false"
          :empData="editEmp"
          :managersList="managers"
          :branches="branches"
          :teams="teams"
          :roleList="roles"
          @refreshEmployees="(updatedUser) => refreshEmployeeAfterEdit(updatedUser)"
          @cancelDrawer="cancelUserInfo"
        />
      </v-dialog>

      <v-dialog v-model="showConfirmationDialog" max-width="700" persistent>
        <v-card>
          <v-card-title class="text-h6 justify-center text-[#6B6D78]"
            >Are you sure you want to assign {{ roleName }} role to
            {{ employeeFullName }}?</v-card-title
          >
          <v-card-text class="text-center mt-1">
            Kindly confirm if you want to assign this role. <br />
            {{ alertText }}
          </v-card-text>
          <v-card-actions class="flex justify-center mb-2">
            <v-btn outlined @click="cancelUpdate" class="mr-4"
              ><span class="text-black normal-case">Cancel</span></v-btn
            >
            <v-btn
              :color="$vuetify.theme.currentTheme.primary"
              class="white--text normal-case"
              @click="confirmUpdate"
              >Confirm</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="mapDialog" max-width="95vw" scrollable>
        <div class="bg-white">
          <div class="flex justify-between items-center p-3">
            <div class="flex items-center gap-2">
              <v-icon color="primary">mdi-map-marker-radius</v-icon>
              <h3 class="text-xl font-semibold text-gray-800" v-if="currentMapUserId">
                Live Field Force Tracking
              </h3>
              <h3 class="text-xl font-semibold text-gray-800" v-else>Last Location Captured</h3>
              <v-divider vertical v-if="currentMapUserId"></v-divider>
              <p class="font-semibold mb-0" v-if="currentMapUserId">Filter By :</p>
              <div class="flex items-center gap-2">
                <v-menu
                  v-if="currentMapUserId"
                  v-model="mapDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="mapDate"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      dense
                      hide-details
                      outlined
                      v-bind="attrs"
                      v-on="on"
                      class="pt-0 mt-0"
                      placeholder="Select Date"
                      label="Select Date"
                      style="max-width: 300px"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="mapDate"
                    @input="onMapDateChange"
                    no-title
                    scrollable
                    :min="minDate"
                  >
                  </v-date-picker>
                </v-menu>
              </div>
            </div>
            <v-btn icon @click="mapDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-divider></v-divider>
          <MapView
            v-if="mapDialog"
            :userLiveLocation="userLiveLocation"
            :focusedUserId="currentMapUserId"
            :totalUsers="totalLiveLocationUsers"
            :isLoading="loadingMapData"
            :currentPage="mapPage"
            :itemsPerPage="mapLimit"
            :showHistoryDetails="false"
            @close="mapDialog = false"
            @refresh="() => fetchLiveLocations(currentMapUserId)"
            @page-change="handleMapPageChange"
          />
        </div>
      </v-dialog>

      <v-dialog
        v-model="showLeadManagement"
        max-width="76rem"
        :fullscreen="$vuetify.breakpoint.smAndDown"
      >
        <EmployeeLeaves
          :employeeData="employeeData"
          :employeeImage="employeeImage"
          :orgAttendanceSettings="orgAttendanceSettings"
          @handleLeaveClose="handleLeaveManagementDialog"
        />
      </v-dialog>

      <v-dialog v-model="showPayrollActionDialog" max-width="90rem" class="bg-white">
        <ActitiveTabcomponent
          :tabs="payrollTabs"
          :defaultTab="activePayrollTab"
          insideDialog
          class="bg-white"
          @tab-changed="(val) => (activePayrollTab = val)"
        >
          <template slot="tab-buttons">
            <div class="flex items-center gap-2">
              <v-btn
                v-if="activePayrollTab === 'salarySlip'"
                color="primary"
                small
                outlined
                @click="downloadSalarySlip"
                :loading="downloading"
                class="normal-case mt-1"
              >
                <v-icon left>mdi-download</v-icon> Download
              </v-btn>
              <v-icon @click="showPayrollActionDialog = false">mdi-close</v-icon>
            </div>
          </template>
          <template slot="salarySlip">
            <EmployeeSalarySlip
              ref="salarySlipRef"
              :branch="findBranch(selectUserExpense)"
              :employeeImage="employeeImage"
              :employeeData="selectUserExpense"
              @handleSalarySlipClose="handleSalarySlipDialog"
              @downloading="(val) => (downloading = val)"
            />
          </template>
          <template slot="reimbursement">
            <TaxSavingDocumentUpload
              :employeeData="selectUserExpense"
              @handleDocumentRequiredClose="showPayrollActionDialog = false"
            />
          </template>
          <template slot="taxDeclarations">
            <TaxDeclarations
              :employeeData="selectUserExpense"
              @close="showPayrollActionDialog = false"
            />
          </template>
          <template slot="taxPaid">
            <PayrollHistory type="tax" :employeeData="selectUserExpense" />
          </template>
          <template slot="pfPaid">
            <PayrollHistory type="pf" :employeeData="selectUserExpense" />
          </template>
        </ActitiveTabcomponent>
      </v-dialog>

      <UpdateCases
        :visible.sync="showDialog"
        :handleShowDialog="handleShowDialog"
        :items="selected"
        :teams="teams"
        :dialogLable="dialogLable"
        :branches="branches"
        :departments="departments"
        :designations="designations"
      >
      </UpdateCases>

      <!-- Add Employee Dialog -->
      <v-dialog
        v-model="showAddEmployeeDialog"
        max-width="900px"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        persistent
      >
        <EmployeeAdd
          :disabled="false"
          :empData="null"
          :managersList="managers"
          :branches="branches"
          :teams="teams"
          :roleList="roles"
          @refreshEmployees="(newUser) => refreshEmployee(newUser)"
          @cancelDrawer="closeAddEmployeeDialog"
        />
      </v-dialog>

      <v-dialog v-model="showEmployeeHistoryDialog" width="90rem" persistent>
        <EmployeeHistory
          :visible="false"
          :employee="employeeData"
          @handleEmployeeHistoryClose="showEmployeeHistoryDialog = false"
        />
      </v-dialog>

      <v-dialog
        v-model="showEmployeeDigitalProfileDialog"
        width="70rem"
        persistent
        :fullscreen="$vuetify.breakpoint.smAndDown"
      >
        <DigitalProfile
          :visible="true"
          :employee="employeeData"
          @handleDigitalProfileClose="showEmployeeDigitalProfileDialog = false"
        />
      </v-dialog>

      <!-- Assign Dialog - Removed as we're only keeping Assign Location -->
      <FloatingActionBar
        :selected="selected"
        :total-items="totalItems"
        v-iam-disabled="'organization.employees.update'"
        :bulk-updating="bulkUpdating"
        :showDeleteAll="false"
        :show-clear="false"
        :showActiveSelected="true"
        @select-all="handleShowDialogDeleteAll('delete')"
        @assign-location="handleAssignLocationClick"
        @assign-department="handleAssignDepartmentClick"
        @toggle-active-inactive="handleBulkStatusUpdate"
      />

      <!-- navigation drawer -->
      <!-- <v-navigation-drawer width="50%" v-model="rightDrawer" v-bind:right="right" fixed>
        <EmployeesActionTabs
          v-on:refreshEmployeeAction="(newUser) => refreshEmployee(newUser)"
          v-on:cancelDrawerAction="cancelDrawer"
          :managersList="managers"
          :roleList="roles"
          @callParentFunction="downloadDemo('xlsx')"
        />
      </v-navigation-drawer> -->
    </div>
  </div>
</template>
<script>
import {
  debounce,
  hasPermission,
  fullName,
  isModuleFeatureAllowed,
  isAnyFeatureAllowed,
  convertToTitleCase
} from '@/utils/common'
import { hasIamPermission } from '@/utils/iamEvaluator'
import { demoEmployeesData } from '@/utils/demoFormat'
import ActionButton from '@/components/ActionButton'
import MapView from '@/components/Workforce/EmployeeManagement/MapView'
import FloatingActionBar from '@/components/Workforce/CollectionManagement/CaseModules/FloatingActionBar.vue'
import EmployeeLeaves from '@/components/Workforce/EmployeeManagement/LeaveManagement/EmployeeLeaves'
import EmployeeSalarySlip from '@/components/Workforce/EmployeeManagement/LeaveManagement/EmployeeSalarySlip'
import {
  roleUpdateSuccess,
  roleUpdateError,
  employeeImportSuccess,
  assignEmployeeMessage,
  assignEmployeeError
} from '@/utils/toastMessages'
import EmployeeAdd from '@/components/Workforce/EmployeeManagement/EmployeeAdd.vue'
import NoData from '@/components/NoData.vue'
import UpdateCases from '@/components/Workforce/CollectionManagement/CaseModules/UpdateCases.vue'
import EmployeeInfo from '@/components/EmployeeInfo.vue'
import permissionData from '@/utils/permissions'
import { axiosUrl } from '@/utils/workforceApi'
import { employeeTypes, UpdateCaseDialogLabels } from '@/utils/workforce/constants'
import UserAvatar from '@/components/UserAvatar.vue'
import TaxSavingDocumentUpload from '@/components/Workforce/EmployeeManagement/DocumentRequired.vue'
import EmployeeHistory from '@/components/Workforce/EmployeeManagement/EmployeeHistory.vue'
import DigitalProfile from '@/components/Workforce/EmployeeManagement/DigitalProfile.vue'
import TreeNode from '@/components/Workforce/EmployeeManagement/TreeNode.vue'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import TaxDeclarations from '@/components/Workforce/EmployeeManagement/TaxDeclarations.vue'
import PayrollHistory from '@/components/Workforce/EmployeeManagement/PayrollHistory.vue'
import TableWrapper from '@/components/UI/TableWrapper.vue'
import EmployeesPage_mobile from './EmployeesPage_mobile.vue'

export default {
  layout: 'Workforce/default',
  name: 'EmployeesPage',
  components: {
    TableWrapper,
    NoData,
    EmployeeInfo,
    EmployeeAdd,
    ActionButton,
    MapView,
    EmployeeLeaves,
    TaxSavingDocumentUpload,
    EmployeeSalarySlip,
    UpdateCases,
    FloatingActionBar,
    UserAvatar,
    EmployeeHistory,
    DigitalProfile,
    TreeNode,
    ActitiveTabcomponent,
    TaxDeclarations,
    PayrollHistory,
    EmployeesPage_mobile
  },
  provide() {
    return {
      parentPage: this
    }
  },
  props: {
    viewOrgTree: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loginUser: this.$storage.getUniversal('user'),
      orgCode: this.$storage.getUniversal('organization_code'),
      userData: this.$store.state.wfmPermissions,
      options: { sortBy: [], sortDesc: [], itemsPerPage: 10, page: 1 },
      totalEmployees: 0,
      currentPage: 1,
      isLoading: true,
      lastFetchedParams: null,
      searchEmployee: '',
      totalItems: 0,
      showLeadManagement: false,
      filterVisible: false,
      selectedTeamName: null,
      addressLine1: '',
      right: true,
      mapDialog: false,
      showConfirmationDialog: false,
      showEmployeeDigitalProfileDialog: false,
      loadingImport: false,
      downloadMenu: false,
      selectedRoleFilter: null,
      selectedDepartment: null,
      selectedDesignation: null,
      selectedStatus: 'ACTIVE',
      employeeData: null,
      filterName: '',
      employees: [],
      inActiveUsers: [],
      showEmployeeHistoryDialog: false,
      userLiveLocation: [],
      selected: [],
      employeeFullName: '',
      editEmp: null, // Keep as null initially
      editDialogKey: 0, // Key to force re-creation of the EmployeeAdd component
      employeeDetails: {},
      openEditEmployee: false,
      roleName: '',
      tempRole: null,
      tempManager: null,
      tempStatus: null,
      userFirstName: '',
      userLastName: '',
      selectedBranchName: null,
      previousRoles: {},
      showFilters: false,
      roles: [],
      status: ['ACTIVE', 'INACTIVE', 'CLOSED'],
      alertText: '',
      employeeImage: '',
      waNumber: '',
      dialogLable: '',
      dialogBranchLable: '',
      showDialog: false,
      managers: [],
      teams: [],
      branches: [],
      departments: [],
      designations: [],
      travelArray: [],
      allowanceArray: [],
      orgAttendanceSettings: {},
      selectUserExpense: null,
      availableTemplates: [],
      errorMessage: '',
      csvFormat: this.generateCsvHeader(demoEmployeesData),

      employeesFormat: demoEmployeesData,
      employeeTypes: [],
      activeStep: 1,
      showMobile: false,
      showEmail: false,
      previousEmployeeId: '',
      employeeIdMessage: '',
      isEmployeeIdAvailable: false,
      isEmployeeIdValid: true,

      selectedCount: 1,
      isEdit: false,
      isModalOpen: false,
      tempEmpId: null,
      showAddEmployeeDialog: false,
      // Add bulkUpdating property for FloatingActionBar

      bulkUpdating: false,
      orgLimit: null,
      downloading: false,
      showPayrollActionDialog: false,
      activePayrollTab: 'salarySlip',
      payrollTabs: [
        {
          key: 'salarySlip',
          label: 'Salary Slip',
          icon: 'mdi-cash-multiple',
          component: 'EmployeeSalarySlip'
        },
        {
          key: 'reimbursement',
          label: 'Reimbursement',
          icon: 'mdi-file-document-outline',
          component: 'TaxSavingDocumentUpload'
        },
        {
          key: 'taxDeclarations',
          label: 'Tax Declarations',
          icon: 'mdi-file-document-outline',
          component: 'TaxDeclarations'
        },
        {
          key: 'taxPaid',
          label: 'Tax Paid',
          icon: 'mdi-file-document-outline',
          component: 'TaxDeclarations'
        },
        {
          key: 'pfPaid',
          label: 'PF Paid',
          icon: 'mdi-file-document-outline',
          component: 'TaxDeclarations'
        }
      ],
      pollingInterval: null,
      currentMapUserId: null,
      mapPage: 1,
      mapLimit: 10,
      totalLiveLocationUsers: 0,
      loadingMapData: false,
      mapDate: new Date().toISOString().substr(0, 10),
      mapDateMenu: false
    }
  },
  watch: {
    mapDialog(val) {
      if (!val) {
        this.cancelPolling()
      }
    },
    'editEmp.employeeId': function (newVal) {
      if (newVal && this.previousEmployeeId && newVal !== this.previousEmployeeId) {
        this.checkEmployeeIdAvailability(newVal)
        this.tempEmpId = newVal
      } else if (!newVal) {
        this.checkEmployeeIdAvailability(newVal)
        this.isEmployeeIdAvailable = false
      } else {
        this.employeeIdMessage = ''
      }
    },
    '$route.query.search': function (newQuery) {
      try {
        this.filterName = newQuery || ''
        this.fetchEmployees(true)
      } catch (error) {
        console.error('Error updating employee search:', error)
      }
    },
    filterName(val) {
      if (!val && this.$route.query.search) {
        const query = { ...this.$route.query }
        delete query.search
        this.$router.replace({ query }).catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },
    jumpToStep(newStep) {
      this.activeStep = newStep
    },
    options: {
      handler() {
        this.fetchEmployees()
      },
      deep: true
    },
    userLiveLocation: {
      handler(newVal) {
        this.userLiveLocation = newVal
      },
      deep: true
    },
    selectUserExpense: {
      handler(newVal) {
        if (newVal) {
          if (
            this.selectUserExpense?.travelModes?.length ||
            this.selectUserExpense?.allowances?.length
          ) {
            this.travelArray = [...this.selectUserExpense?.travelModes]
            this.allowanceArray = [...this.selectUserExpense?.allowances]
          }
        }
      },
      deep: true
    },
    selectedTeamName() {
      this.updateSelectedCount()
    },
    selectedRoleFilter() {
      this.updateSelectedCount()
    },
    selectedDepartment() {
      this.updateSelectedCount()
    },
    selectedDesignation() {
      this.updateSelectedCount()
    },
    selectedStatus(val) {
      if (val) {
        this.updateSelectedCount()
      }
    },
    '$attrs.roles': {
      handler(val) {
        if (val && val.length > 0) {
          this.roles = val
        }
      },
      immediate: true
    },
    '$attrs.branches': {
      handler(val) {
        if (val && val.length > 0) {
          this.branches = val
        }
      },
      immediate: true
    },
    '$attrs.teams': {
      handler(val) {
        if (val && val.length > 0) {
          this.teams = val
        }
      },
      immediate: true
    },
    '$attrs.departments': {
      handler(val) {
        if (val && val.length > 0) {
          this.departments = val
        }
      },
      immediate: true
    },
    '$attrs.designations': {
      handler(val) {
        if (val && val.length > 0) {
          this.designations = val
        }
      },
      immediate: true
    }
  },
  computed: {
    rowClass() {
      return (item, index) => {
        return index % 2 === 0 ? 'even-row' : 'odd-row'
      }
    },
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },

    wrapperHeaders() {
      return [
        {
          text: '',
          value: 'select',
          width: '50px',
          align: 'center'
        },
        ...this.tableHeaders
      ]
    },
    totalPages() {
      return Math.ceil((this.totalEmployees || 0) / (this.options.itemsPerPage || 10))
    },
    isAllSelected() {
      return (
        this.employees &&
        this.employees.length > 0 &&
        this.selected.length === this.employees.length
      )
    },
    tableHeaders() {
      const action = {
        text: 'Action',
        value: 'action',
        width: '10%',
        sortable: false
      }
      const teamCol = {
        text: 'Team',
        value: 'team',
        width: '15%',
        sortable: false
      }

      const branchCol = {
        text: 'Branch',
        value: 'branch',
        width: '15%',
        sortable: false
      }

      const tableHeaders = [
        {
          text: 'Name',
          value: 'firstName',
          width: '20%'
        },
        // {
        //   text: 'Mobile',
        //   value: 'mobile',
        //   width: '10%',
        //   sortable: false
        // },
        // {
        //   text: 'Email ID',
        //   value: 'email',
        //   width: '15%'
        // },
        {
          text: 'Department',
          value: 'department',
          width: '5%',
          sortable: false
        },
        {
          text: 'Designation',
          value: 'designation',
          width: '5%',
          sortable: false
        },
        ...(this.isFeatureVisible('ASSIGN_TEAM') && this.isAnyTeamSettingAllowed ? [teamCol] : []),
        ...(this.isFeatureVisible('ASSIGN_BRANCH') && this.isAnyBranchSettingAllowed
          ? [branchCol]
          : []),
        // { text: "Team", value: "team", width: "15%", sortable: false },
        // { text: "Branch", value: "branch", width: "15%", sortable: false },
        {
          text: 'Role',
          value: 'role',
          width: '10%',
          sortable: false
        },
        {
          text: 'Manager',
          value: 'directManager',
          width: '10%',
          sortable: false
        },
        {
          text: 'Status',
          value: 'status',
          width: '10%',
          sortable: false
        },
        action
      ]
      return tableHeaders
    },
    // shouldCheckboxVisible() {
    //   return (
    //     this.isFeatureVisible('BULK_ASSIGN_TEAMS') ||
    //     this.isFeatureVisible('BULK_ASSIGN_BRANCH') ||
    //     this.isFeatureVisible('BULK_ASSIGN_SALARY_TEMPLATE')
    //   )
    // },

    getManagerFullName() {
      return (manager) => {
        if (!manager || !manager._id || manager._id === 'UNASSIGNED') return 'Unassigned'
        if (manager.firstName) {
          return `${manager.firstName} ${manager.lastName || ''}`.trim()
        }
        const name = manager.fullName || 'Unknown'
        const designation = manager.designation?.designationName || 'No Designation'
        return `${name} (${designation})`
      }
    },
    isOrgAdmin() {
      return this.loginUser?.role?.includes('WFM_ADMIN')
    },
    monthlySalary() {
      return this.editEmp ? this.employeeMonthlySalary(this.editEmp?.employmentDetails?.ctc) : 0
    },
    jumpToStep() {
      if (!this.editEmp) {
        return 1
      } else if (!this.isProfileComplete) {
        return 1
      } else if (!this.isEmployeeDetailsComplete) {
        return 2
      } else if (!this.isCurrentAddressComplete) {
        return 3
      } else {
        return 4
      }
    },
    isProfileComplete() {
      return (
        this.editEmp &&
        this.editEmp.firstName &&
        this.editEmp.lastName &&
        !!this.editEmp.maskedEmail &&
        !!this.editEmp.maskedMobileNumber
      )
    },
    isEmployeeDetailsComplete() {
      return (
        this.employeeDetails.employee_id &&
        this.employeeDetails.employee_type &&
        this.employeeDetails.department &&
        this.employeeDetails.designation &&
        this.employeeDetails.salary &&
        this.employeeDetails.uanNumber &&
        this.employeeDetails.panNumber &&
        this.employeeDetails.bankName &&
        this.employeeDetails.ifscCode &&
        this.employeeDetails.bankAccountNumber
      )
    },
    isCurrentAddressComplete() {
      return (
        this.editEmp &&
        this.editEmp.addressLine1 &&
        this.editEmp.city &&
        this.editEmp.state &&
        this.editEmp.country &&
        this.editEmp.pinCode
      )
    },
    isRolesAndManagementComplete() {
      return this.editEmp && this.editEmp.wfmRole && this.editEmp.reportesTo && this.editEmp.status
    },
    employeeIdRule() {
      return () => {
        if (this.employeeIdMessage) {
          return this.isEmployeeIdAvailable ? true : this.employeeIdMessage
        }
        return true
      }
    },
    isAnyBranchSettingAllowed() {
      return isAnyFeatureAllowed('BRANCH_MANAGEMENT')
    },
    isAnyTeamSettingAllowed() {
      return isAnyFeatureAllowed('TEAM_MANAGEMENT')
    },
    isAnySalarySettingAllowed() {
      return isAnyFeatureAllowed('SALARY_MANAGEMENT')
    },
    isAnyLeaveSettingAllowed() {
      return isAnyFeatureAllowed('LEAVE_MANAGEMENT')
    },
    isAnyClaimSettingAllowed() {
      return isAnyFeatureAllowed('CLAIM_MANAGEMENT')
    },
    minDate() {
      const orgData = this.$storage.getUniversal('orgData')
      if (orgData && orgData.valid_from) {
        return this.$dayjs(orgData.valid_from).format('YYYY-MM-DD')
      }
      return null
    }
  },

  methods: {
    hasIamPermission,
    downloadSalarySlip() {
      if (this.$refs.salarySlipRef) {
        this.$refs.salarySlipRef.downloadAsPDF()
      }
    },
    async checkEmployeeIdAvailability(employeeId) {
      try {
        const params = {
          employeeId: String(employeeId)
        }
        const response = await this.$axios.get(axiosUrl.employees.getEmployees, {
          params
        })

        if (!employeeId) {
          this.employeeIdMessage = 'Employee ID is required'
          this.isEmployeeIdAvailable = false
          return
        } else if (response.data.success && response.data.users.length > 0) {
          this.employeeIdMessage = 'Employee ID is already taken'
          this.isEmployeeIdAvailable = false
        } else {
          this.employeeIdMessage = 'Employee ID is available'
          this.isEmployeeIdAvailable = true
        }
      } catch (error) {
        console.error('Error checking employee ID:', error)
        this.employeeIdMessage = 'Error checking employee ID'
        this.isEmployeeIdAvailable = false
      }
    },
    handleLeaveManagementDialog() {
      this.showLeadManagement = false
    },
    handleSalarySlipDialog() {
      // this.showSalarySlipDialog = false
      // Just close the payroll dialog if needed, or maybe just do nothing?
      // Usually close the whole dialog on success or cancel
      this.showPayrollActionDialog = false
    },
    openModal() {
      this.isModalOpen = true
    },
    closeModal() {
      this.isModalOpen = false
    },
    clearAddress() {
      if (this.editEmp) {
        this.editEmp.addressLine1 = ''
        this.editEmp.city = ''
        this.editEmp.state = ''
        this.editEmp.country = ''
        this.editEmp.pinCode = ''
      }
      this.selectedAddress = ''
    },
    toggleSelectAll(val) {
      if (val) {
        this.selected = [...this.employees]
      } else {
        this.selected = []
      }
    },
    isRowSelected(item) {
      return this.selected.some((selectedItem) => selectedItem._id === item._id)
    },
    toggleRowSelected(item, isSelected) {
      if (isSelected) {
        if (!this.isRowSelected(item)) {
          this.selected.push(item)
        }
      } else {
        this.selected = this.selected.filter((selectedItem) => selectedItem._id !== item._id)
      }
    },
    async importEmployees() {
      try {
        this.loadingImport = true
        await this.$axios.get(axiosUrl.employees.importEmployees)

        this.$toast.success(employeeImportSuccess)
        await this.fetchEmployees(true)
      } catch (error) {
        console.error('Error importing employees:', error)
      } finally {
        this.loadingImport = false
      }
    },
    async fetchInActiveEmployees() {
      try {
        const response = await this.$axios.get(axiosUrl.employees.inActiveEmployees)
        this.inActiveUsers = response.data.employees
      } catch (error) {
        console.log(error.message)
      }
    },
    handleInput: debounce(function () {
      this.currentPage = 1
      this.options.page = 1
      this.fetchEmployees(true)
    }, 1000),
    newRoleId(employee) {
      this.tempRole = employee.wfmRole
    },
    newManagerId(manager) {
      this.tempManager = manager.reportesTo
    },
    newStatusId(status) {
      this.tempStatus = status.status
    },
    getPartnerStatusClr(status) {
      const statuses = {
        ACTIVE: 'text-green-500 font-medium',
        INACTIVE: 'text-red-500 font-medium',
        CLOSED: 'text-gray-500 font-medium'
      }

      return statuses[status?.toUpperCase()] || 'text-gray-500 font-medium'
    },
    employeeMonthlySalary(salary) {
      return Math.floor(salary / 12)
    },
    async getConfig() {
      try {
        const response = await this.$axios.get('/orgSetup/attendance-settings')

        const data = response.data?.attendanceSettings
        this.orgAttendanceSettings = data
        return data || {}
      } catch (error) {
        console.error('Error fetching attendanceSettings:', error)
      }
    },
    async prepareEmployeeParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      const params = {
        page: page,
        limit: itemsPerPage,
        search: this.filterName || null,
        // skipHierarchy: "true",
        sortOrder: 'asc'
      }

      if (sortBy.length > 0) {
        params.sortBy = sortBy[0]
      } else {
        params.sortBy = 'firstName'
      }

      if (sortDesc.length > 0) {
        params.sortOrder = sortDesc[0] ? 'desc' : 'asc'
      }

      if (this.selectedRoleFilter) {
        params.wfmRole = this.selectedRoleFilter
      }

      if (this.searchEmployee) {
        params.search = this.searchEmployee
      }

      if (this.selectedTeamName) {
        params.team = this.selectedTeamName
      }

      if (this.selectedBranchName) {
        params.branch = this.selectedBranchName
      }

      if (this.selectedDepartment) {
        params.department = this.selectedDepartment
      }

      if (this.selectedDesignation) {
        params.designation = this.selectedDesignation
      }

      if (this.selectedStatus) {
        params.status = this.selectedStatus
      }

      if (this.loginUser?.branch && !this.loginUser?.role?.includes('WFM_ADMIN')) {
        // params.branch = this.loginUser?.branch;
      }

      return params
    },

    async fetchEmployees(force = false) {
      try {
        const params = await this.prepareEmployeeParams()
        const paramsStr = JSON.stringify(params)

        if (!force && this.lastFetchedParams === paramsStr) {
          return
        }
        this.lastFetchedParams = paramsStr

        this.isLoading = true

        const response = await this.$axios.get(axiosUrl.employees.getEmployees, {
          params
        })
        if (response.data.success) {
          this.totalEmployees = response.data.pagination
            ? response.data.pagination.totalCount
            : this.employees.length
          this.employees = response.data.users.map((emp) => ({
            ...emp,
            showCheckbox: false
          }))
        }
      } catch (error) {
        console.error('Error fetching employees:', error)
      } finally {
        this.isLoading = false
      }
    },
    async fetchAllEmployeeDataInChunks() {
      const allEmployees = []
      const itemsPerPage = 50

      try {
        const initialParams = {
          ...this.prepareEmployeeParams(),
          page: 1,
          limit: itemsPerPage
        }
        const initialResponse = await this.$axios.get(axiosUrl.employees.getEmployees, {
          params: initialParams
        })

        if (!initialResponse.data.success) return allEmployees

        allEmployees.push(...initialResponse.data.users)
        const totalPages = initialResponse.data.pagination.totalPages

        if (totalPages === 1) return allEmployees

        const promises = []
        for (let page = 2; page <= totalPages; page++) {
          const params = {
            ...this.prepareEmployeeParams(),
            page,
            limit: itemsPerPage
          }
          promises.push(this.$axios.get(axiosUrl.employees.getEmployees, { params }))
        }

        const responses = await Promise.allSettled(promises)

        responses.forEach((result) => {
          if (result.status === 'fulfilled' && result.value.data.success) {
            allEmployees.push(...result.value.data.users)
          }
        })
        return allEmployees
      } catch (error) {
        console.error('Error fetching all employee data in chunks:', error)
        return allEmployees
      }
    },
    async exportAllEmployees() {
      try {
        const XLSX = require('xlsx')

        const allEmployees = await this.fetchAllEmployeeDataInChunks()

        const headers = [
          ['Name', 'Employee ID', 'Mobile', 'Email', 'Team', 'Branch', 'Status', 'Role', 'Manager']
        ]

        const rows = allEmployees.map((employee) => [
          employee.fullName,
          employee.employeeId,
          employee.mobile,
          employee.maskedEmail,
          employee.team?.teamName || '-',
          employee.branch?.branchName || '-',
          employee.status,
          employee.wfmRole || '-',
          employee.reportesTo ? this.getManagerFullName(employee.reportesTo) : ''
        ])

        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows])
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'All Employees')

        XLSX.writeFile(workbook, 'All_Employee_Data.xlsx')

        this.$toast.success('Successfully exported all employee data!')
      } catch (error) {
        console.error('Error exporting all employee data:', error)
        this.$toast.error('Failed to export employee data.')
      }
    },
    handlePageChange(newPage) {
      this.pagination.page = newPage
      this.fetchEmployees()
    },
    getEmployeeImage(item) {
      if (item?.systemUserId?.profileImage) {
        return `${process.env.VUE_APP_S3_BASE_URL}${item?.systemUserId?.profileImage}`
      } else {
        if (item?.gender === 'M') {
          return require('~/assets/img/profileMale.png')
        } else if (item?.gender === 'F') {
          return require('~/assets/img/profileFemale.png')
        } else {
          return require('~/assets/img/profileOther.png')
        }
      }
    },
    findBranch(item) {
      return this.branches.find((el) => el._id === item?.branch)
    },
    async updateRole(employee) {
      const selectedRoleId = employee.wfmRole
      if (!selectedRoleId) return
      // const previousRoleId = this.previousRoles[employee._id];
      this.employeeFullName = employee.firstName + ' ' + employee.lastName
      const selectedRole = this.roles.find((role) => role._id === selectedRoleId)
      this.roleName = selectedRole ? selectedRole.role : 'unassigned'
      // const previousRole = this.roles.find(
      //   (role) => role._id === previousRoleId
      // );
      const hasAdminAccess =
        selectedRole && selectedRole.extraPrivileges.includes('WORKFORCE_ADMIN_ACCESS')

      if (hasAdminAccess) {
        this.alertText = 'This Role has Workforce management dashoboard access.'
      } else if (selectedRole) {
        this.alertText = ''
      } else {
        this.alertText = 'This will clear all of the access.'
      }

      await this.getOrgSalary(selectedRole?._id)
      this.employeeToUpdate = employee
      this.showConfirmationDialog = true
    },

    confirmUpdate() {
      this.showConfirmationDialog = false

      if (this.employeeToUpdate) {
        this.performRoleUpdate(this.employeeToUpdate)
        this.employeeToUpdate = null
      }
    },
    cancelUpdate() {
      this.showConfirmationDialog = false
      if (this.employeeToUpdate) {
        this.employeeToUpdate.wfmRole = this.tempRole
        this.employeeToUpdate = null
      }
    },
    async performRoleUpdate(employee) {
      let payload = {}
      const hasTravelInfo = this.travelArray?.length > 0 && this.travelArray[0]?.pricePerKm
      const hasAllowanceInfo = this.allowanceArray?.length > 0 && this.allowanceArray[0]?.amount

      if (hasTravelInfo || hasAllowanceInfo) {
        payload.userInfo = {
          travelModes: this.travelArray,
          allowances: this.allowanceArray
        }
        // Also include the role update if applicable
        payload.wfmRoleId = employee.wfmRole === 'UNASSIGNED' ? null : employee.wfmRole

        this.$axios
          .patch(`/users/${employee._id}?action=UPDATE_USER_INFO`, payload)
          .then(() => {
            this.$toast.success(roleUpdateSuccess)
            this.fetchManager()
            // Clear arrays after successful update to prevent cross-contamination
            this.travelArray = []
            this.allowanceArray = []
          })
          .catch((error) => {
            console.error(roleUpdateError, error)
            this.$toast.error(roleUpdateError)
          })
      } else {
        this.previousRoles[employee._id] = employee.wfmRole
        payload.wfmRoleId = employee.wfmRole === 'UNASSIGNED' ? null : employee.wfmRole

        this.$axios
          .patch(`/users/${employee._id}`, payload)
          .then(() => {
            this.$toast.success(roleUpdateSuccess)
            this.fetchManager()
          })
          .catch((error) => {
            console.error(roleUpdateError, error)
            this.$toast.error(roleUpdateError)
          })
      }
    },
    async fetchManager() {
      try {
        const response = await this.$axios.get(axiosUrl.employees.getManagers, {
          params: { limit: '*' }
        })
        this.managers = response.data.users
      } catch (error) {
        console.log(error, 'Error fecthing managers')
      }
    },
    directManager(managerId) {
      const manager = this.managers.find((manager) => manager._id === managerId)
      return manager ? fullName(manager) : 'No Manager'
    },

    async assignToManager(employee) {
      const reportesToId = employee.reportesTo?._id || employee.reportesTo
      if (!reportesToId) return

      try {
        const response = await this.$axios.patch(
          `/users/${employee._id}?action=UPDATE_REPORTING_TO`,
          {
            reportesToId: reportesToId === 'UNASSIGNED' ? null : reportesToId
          }
        )
        if (reportesToId === 'UNASSIGNED') {
          employee.reportesTo = null
        }
        const reportees = response.data.user.reportesTo
        this.$toast.success(assignEmployeeMessage(employee, reportees))
      } catch (error) {
        console.error(assignEmployeeError, error)
        this.$toast.error(assignEmployeeError)
      }
    },
    async fetchRoles() {
      try {
        const response = await this.$axios.get(axiosUrl.role.fetchRole)
        if (response.data.success) {
          this.roles = response.data.role
        } else {
          console.error('Error fetching roles:', response.data.message)
        }
      } catch (error) {
        console.error('Error fetching roles:', error)
      }
    },
    toggleFilters() {
      this.showFilters = !this.showFilters
    },
    getRoleDropdown(_employee) {
      return [{ _id: 'UNASSIGNED', role: 'Unassigned' }, ...this.roles]
    },
    convertToTitleCase(item) {
      return convertToTitleCase(item)
    },
    generateCsvHeader(data) {
      if (data.length === 0) {
        return []
      }
      const Item = data[0]
      return Object.keys(Item)
    },
    // async downloadDemo(format) {
    //   this.downloadFile(format, this.employeesFormat, 'Sample_User_Format')
    // },
    async fetchAndDownloadInactive(format) {
      try {
        await this.fetchInActiveEmployees()
        this.downloadFile(format, this.inActiveUsers, 'Non-Engaged_User_Data')
      } catch (error) {
        console.error('Error fetching and downloading In Active User data:', error)
      }
    },
    async saveUserInfo() {
      this.isEdit = true
      try {
        if (this.editEmp) {
          this.assignToManager(this.editEmp)
        }
        if (this.employeeToUpdate) {
          this.performRoleUpdate(this.employeeToUpdate)
        }
        await this.cancelUserInfo()
        await this.updateEmployeeDetails()
        this.$toast.success('Employee details updated successfully !!')
        this.isEdit = false
        this.openEditEmployee = false
        this.previousEmployeeId = ''
      } catch (error) {
        console.log(error)
      }
    },
    // cancelUserInfo() {
    //   // this.importEmployees();
    //   this.tempManager = null
    //   this.employeeToUpdate = null
    //   this.openEditEmployee = false
    //   this.editEmp = null // Changed from {} to null
    //   this.previousEmployeeId = ''
    //   this.userFirstName = ''
    //   this.userLastName = ''
    // },
    downloadFile(format, data, fileName) {
      if (format === 'csv') {
        this.downloadCSV(data, `${fileName}.csv`)
      } else if (format === 'xlsx') {
        this.downloadXLSX(data, `${fileName}.xlsx`)
      }
    },
    downloadCSV(data, fileName) {
      if (!data || data.length === 0) {
        console.error('No data to download.')
        return
      }

      const csvRows = []
      const headerRow = Object.keys(data[0]).join(',')
      csvRows.push(headerRow)

      data.forEach((item) => {
        const rowData = Object.values(item)
          .map((value) => (typeof value === 'string' ? `"${value}"` : value))
          .join(',')
        csvRows.push(rowData)
      })

      const csvContent = csvRows.join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = window.URL.createObjectURL(blob)
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(a.href)
    },
    downloadXLSX(data, fileName) {
      const XLSX = require('xlsx')
      const excelSheet = XLSX.utils.json_to_sheet(data)
      const newBook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(newBook, excelSheet, 'Sheet1')
      XLSX.writeFile(newBook, `${fileName}`)
    },
    // onboardNewEmployees() {
    //   this.rightDrawer = !this.rightDrawer
    // },
    getSelectItems(item) {
      const filteredManagers = this.managers.filter((manager) => {
        return manager._id !== item._id
      })
      return [{ _id: 'UNASSIGNED', firstName: 'Unassigned', lastName: '' }, ...filteredManagers]
    },
    addUserExpense(item) {
      this.showPayrollActionDialog = true
      this.activePayrollTab = 'salarySlip'
      this.selectUserExpense = item
      this.employeeImage = this.getEmployeeImage(item)
    },
    showEmployeeHistory(employee) {
      this.employeeData = employee
      this.showEmployeeHistoryDialog = true
    },
    showDigitalProfile(employee) {
      this.employeeData = employee
      this.showEmployeeDigitalProfileDialog = true
    },
    async editUser(employee) {
      // First, get complete employee details
      let employeeDetails = {}
      try {
        // Make sure we have a valid userId before making the API call
        if (employee.userId) {
          const response = await this.$axios.get(`/user/employeeDetails?userId=${employee._id}`)
          employeeDetails = response.data?.user || {}
        }
      } catch (error) {
        console.log('Error fetching employee details:', error)
        // Continue even if we can't fetch details
      }

      // Prepare employee data for editing with more complete information
      const editEmployeeData = {
        user_id: employee._id, // This is the main employee ID used for updates
        userId: employee.userId, // This is the user ID used for fetching details
        first_name: employee.systemUserId?.firstName,
        last_name: employee.systemUserId?.lastName,
        mobile: employee.systemUserId?.mobile,
        email: employee.systemUserId?.email,
        gender: employee.gender,
        maritalStatus: employee.maritalStatus,
        dob: employee.dob,
        employee_id: employee.employmentDetails?.empId,
        employee_type: employee.employeeType || employee.employmentDetails?.empType,
        department: employee.department || employeeDetails.department,
        designation: employee.designation || employeeDetails.designation,
        salary: employee.ctc || employee?.employmentDetails?.ctc,
        // Address information - formatted to match EmployeeAdd component expectations
        is_both_address_same:
          employeeDetails.is_both_address_same !== undefined
            ? employeeDetails.is_both_address_same
            : true,
        permanent_address_line_1:
          employeeDetails.permanent_address_line_1 || employee.addressLine1 || '',
        permanent_address_line_2: employeeDetails.permanent_address_line_2 || '',
        permanent_address_city: employeeDetails.permanent_address_city || employee.city || '',
        permanent_address_state: employeeDetails.permanent_address_state || employee.state || '',
        permanent_address_country:
          employeeDetails.permanent_address_country || employee.country || '',
        permanent_address_pincode:
          employeeDetails.permanent_address_pincode || employee.pinCode || '',
        current_address_line_1: employeeDetails.current_address_line_1 || '',
        current_address_line_2: employeeDetails.current_address_line_2 || '',
        current_address_city: employeeDetails.current_address_city || '',
        current_address_state: employeeDetails.current_address_state || '',
        current_address_country: employeeDetails.current_address_country || '',
        current_address_pincode: employeeDetails.current_address_pincode || '',
        // Management information
        manager_id: employee.reportesTo?._id,
        wfmRoleId: employee.wfmRole?._id,
        branch: employee.branch,
        team: employee.team?._id
      }

      this.editEmp = editEmployeeData
      if (this.editEmp) {
        this.editEmp.employmentDetails = employee?.employmentDetails
      }

      // Increment the key to force re-creation of the EmployeeAdd component
      this.editDialogKey += 1
      this.openEditEmployee = true
      await this.getOrgSalary(employee.wfmRole)
      this.previousEmployeeId = employee.employeeId
      this.userFirstName = employee.firstName
      this.userLastName = employee.lastName
    },
    cancelUserInfo() {
      // Don't reset editEmp to null here, just close the dialog
      // This allows the data to persist when the dialog is reopened
      this.openEditEmployee = false
      this.previousEmployeeId = ''
      this.userFirstName = ''
      this.userLastName = ''
    },
    // async getEmployeeDetails(userId) {
    //   try {
    //     const response = await this.$axios.get(`/user/employeeDetails?userId=${userId}`)
    //     this.employeeDetails = response.data?.user
    //   } catch (error) {
    //     console.log(error)
    //   }
    // },
    async updateEmployeeDetails(employee) {
      try {
        // Use the passed employee object or fall back to editEmp
        const details = employee || this.editEmp

        if (!details) {
          throw new Error('No employee details provided')
        }

        const payload = {
          employee_id:
            employee?.employeeId ||
            employee?.employmentDetails?.empId ||
            this.tempEmpId ||
            this.editEmp?.employeeId ||
            this.editEmp?.employee_id,
          employee_type:
            details?.employeeType || details?.employmentDetails?.empType || details?.employee_type,
          salary:
            Number(details?.ctc || details?.salary || details?.employmentDetails?.ctc) || undefined,
          monthly_income:
            this.employeeMonthlySalary(
              details?.ctc || details?.salary || details?.employmentDetails?.ctc
            ) || undefined,
          department: details?.department?._id || details?.department,
          designation: details?.designation?._id || details?.designation,
          status: details?.status || details?.systemUserId?.status,
          gender: details?.gender,
          maritalStatus: details?.maritalStatus,
          dob: details?.dob,
          userAddress: {}
        }

        // Only include name fields if they are being updated
        if (employee?.firstName || employee?.lastName || employee?.systemUserId?.firstName) {
          payload.last_name = employee.lastName || employee?.systemUserId?.lastName
          payload.first_name = employee.firstName || employee?.systemUserId?.firstName
          payload.userMobile = employee.mobile || employee?.systemUserId?.mobile
        } else if (
          this.userFirstName !== this.editEmp?.firstName ||
          this.userLastName !== this.editEmp?.lastName
        ) {
          payload.last_name = this.userLastName
          payload.first_name = this.userFirstName
          payload.userMobile = this.editEmp?.mobile
        }

        // Include address if available
        if (employee?.addressLine1 || employee?.systemUserId?.addresses?.length > 0) {
          payload.userAddress.addressLine1 =
            employee.addressLine1 || employee?.systemUserId?.addresses[0]?.addressLine1 || undefined
          payload.userAddress.city =
            employee.city || employee?.systemUserId?.addresses[0]?.city || undefined
          payload.userAddress.state =
            employee.state || employee?.systemUserId?.addresses[0]?.state || undefined
          payload.userAddress.country =
            employee.country || employee?.systemUserId?.addresses[0]?.country || undefined
          payload.userAddress.pinCode =
            Number(employee.pinCode || employee?.systemUserId?.addresses[0]?.pinCode) || undefined
        } else if (this.selectedAddress || this.editEmp?.addressLine1) {
          payload.userAddress.addressLine1 = this.editEmp?.addressLine1 || undefined
          payload.userAddress.city = this.editEmp?.city || undefined
          payload.userAddress.state = this.editEmp?.state || undefined
          payload.userAddress.country = this.editEmp?.country || undefined
          payload.userAddress.pinCode = Number(this.editEmp?.pinCode) || undefined
        }

        await this.$axios.put(`/user/employeeDetails/${details?._id || employee?._id}`, payload)
      } catch (error) {
        console.error('Error in updateEmployeeDetails:', error)
        throw error // Re-throw the error so it can be caught by the caller
      }
    },
    userLeaves(employee) {
      this.employeeData = employee
      this.employeeImage = this.getEmployeeImage(employee)
      this.showLeadManagement = true
    },

    selectUserDelete(item) {
      try {
        this.$axios
          .delete(`/user/${item._id}`)
          .then((response) => {
            const successMessage = response?.data?.message || 'Employee deleted successfully !!'
            this.$toast.success(successMessage)
            this.fetchEmployees(true)
          })
          .catch((error) => {
            console.error(error)
          })
      } catch (error) {
        console.error('Error in deleteLead:', error)
      }
    },
    async refreshEmployee(newUser) {
      // this.rightDrawer = false
      this.showAddEmployeeDialog = false
      this.openEditEmployee = false // Close edit dialog when refresh is called
      await this.importEmployees()
      if (newUser.user_id) {
        try {
          const params = {
            skipHierarchy: 'true',
            userId: newUser.user_id
          }

          const emp = await this.$axios.get(axiosUrl.employees.getEmployees, { params })
          const empId = emp.data?.users[0]?._id
          await this.$axios.patch(`/users/${empId}?action=UPDATE_REPORTING_TO`, {
            reportesToId: newUser.manager?._id || null
          })
          // UPDATE_USER_INFO

          // Optimize the conditional check and axios call into a more concise form
          const hasBranchOrTeam =
            newUser.manager?.branch || newUser.selectedBranch || newUser.selectedTeam

          if (hasBranchOrTeam) {
            const branch = newUser.manager?.branch || newUser.selectedBranch
            const team = newUser.selectedTeam || undefined
            const info = {}
            if (newUser.department) {
              info.department = newUser.department
            }
            if (newUser.designation) {
              info.designation = newUser.designation
            }

            await this.$axios.patch(`/users/${empId}?action=UPDATE_USER_INFO`, {
              userInfo: {
                branch,
                team,
                ...info
              }
            })
          }
          if (newUser.selectedRole?._id) {
            const payload = {
              wfmRoleId: newUser.selectedRole?._id
            }
            this.$axios.patch(`/users/${empId}`, payload)
          }
          await this.fetchEmployees(true)
        } catch (error) {
          console.error(assignEmployeeError, error)
          this.$toast.error(assignEmployeeError)
        }
      }
    },
    async refreshEmployeeAfterEdit(updatedUser) {
      // Close the edit dialog
      this.openEditEmployee = false
      // Don't reset editEmp to null here either

      // Refresh the employee list
      await this.importEmployees()

      // If we have updated user data, we might need to update relationships
      if (updatedUser.user_id || updatedUser.userId) {
        try {
          const params = {
            skipHierarchy: 'true',
            userId: updatedUser.userId || updatedUser.user_id
          }

          let emp
          try {
            emp = await this.$axios.get(`/v3/users`, { params })
          } catch (apiError) {
            console.error('Error fetching user data:', apiError)
            await this.fetchEmployees(true)
            return
          }

          if (!emp.data?.users || emp.data.users.length === 0) {
            console.warn('No user data found, refreshing employee list')
            await this.fetchEmployees(true)
            return
          }

          const empId = emp.data?.users[0]?._id

          if (!empId) {
            console.warn('No valid employee ID found, refreshing employee list')
            await this.fetchEmployees(true)
            return
          }

          if (updatedUser.manager) {
            try {
              await this.$axios.patch(`/users/${empId}?action=UPDATE_REPORTING_TO`, {
                reportesToId: updatedUser.manager._id || null
              })
            } catch (error) {
              console.error('Error updating reporting manager:', error)
            }
          }

          // Update role if provided
          if (updatedUser.selectedRole) {
            try {
              const payload = {
                wfmRoleId: updatedUser.selectedRole._id
              }
              await this.$axios.patch(`/users/${empId}`, payload)
            } catch (error) {
              console.error('Error updating role:', error)
              // Continue with other updates even if this fails
            }
          }

          // Update branch and team if provided
          const hasBranchOrTeam = updatedUser.selectedBranch || updatedUser.selectedTeam
          // console.log({ hasBranchOrTeam })
          if (hasBranchOrTeam || updatedUser.department || updatedUser.designation) {
            try {
              const userInfo = {}
              if (updatedUser.selectedBranch) {
                userInfo.branch = updatedUser.selectedBranch
              }
              if (updatedUser.selectedTeam) {
                userInfo.team = updatedUser.selectedTeam
              }
              if (updatedUser.department) {
                userInfo.department = updatedUser.department
              }
              if (updatedUser.designation) {
                userInfo.designation = updatedUser.designation
              }

              await this.$axios.patch(`/users/${empId}?action=UPDATE_USER_INFO`, {
                userInfo
              })
            } catch (error) {
              console.error('Error updating branch/team:', error)
              // Continue with other updates even if this fails
            }
          }

          await this.fetchEmployees(true)
        } catch (error) {
          console.error(assignEmployeeError, error)
          // Even if there's an error, still refresh the employee list
          await this.fetchEmployees(true)
          // Show a more generic success message since the main update likely succeeded
          this.$toast.success('Employee updated successfully')
        }
      } else {
        // If no user ID, just refresh the list
        await this.fetchEmployees(true)
      }
    },
    // cancelDrawer() {
    //   this.rightDrawer = false
    // },
    // userFullName(item) {
    //   return fullName(item)
    // },
    // isSelected(employee) {
    //   return this.selectedEmployeeIds.has(employee._id)
    // },

    // Func for updated selected array with employee data
    // updateSelected(employee) {
    //   if (this.selectedEmployeeIds.has(employee._id)) {
    //     this.selectedEmployeeIds.delete(employee._id)
    //     // Also remove from selected array if present
    //     const index = this.selected.findIndex((e) => e._id === employee._id)
    //     if (index !== -1) {
    //       this.selected.splice(index, 1)
    //     }
    //   } else {
    //     this.selectedEmployeeIds.add(employee._id)
    //     // Add to selected array if not already present
    //     if (!this.selected.some((e) => e._id === employee._id)) {
    //       this.selected.push(employee)
    //     }
    //   }
    // },
    async openMap(item) {
      this.userLiveLocation = []
      this.mapDialog = true
      this.currentMapUserId = item ? item._id : null

      const today = this.$dayjs().format('YYYY-MM-DD')
      const min = this.minDate

      if (min && this.$dayjs(today).isBefore(this.$dayjs(min))) {
        this.mapDate = min
      } else {
        this.mapDate = today
      }

      this.mapPage = 1
      await this.fetchLiveLocations(this.currentMapUserId)
    },
    onMapDateChange() {
      this.mapDateMenu = false
      this.fetchLiveLocations(this.currentMapUserId)
    },
    async fetchLiveLocations(userId = null) {
      try {
        this.loadingMapData = true
        const url = userId ? `/location-time-series/${userId}` : '/location-time-series'

        let params = {}
        if (userId) {
          params = { date: this.mapDate }
        } else {
          params = {
            page: this.mapPage,
            limit: this.mapLimit
          }
        }

        const response = await this.$axios.get(url, { params })
        const newData = response.data?.data?.data || []

        if (userId) {
          this.userLiveLocation = newData
        } else {
          // If fetching all users, handle pagination
          // Check for total in common API response locations
          this.totalLiveLocationUsers = response.data?.total || response.data?.data?.total || 0
          this.userLiveLocation = newData
        }
      } catch (error) {
        console.error(error)
        this.errorMessage = error.response?.data?.message || 'Error fetching location data'
        if (!this.userLiveLocation.length) {
          this.$toast.error(this.errorMessage)
        }
      } finally {
        this.loadingMapData = false
      }
    },
    handleMapPageChange(newPage) {
      this.mapPage = newPage
      this.fetchLiveLocations(null)
    },
    cancelPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    async fetchTeams() {
      try {
        const response = await this.$axios.get(axiosUrl.teams.getTeams)
        this.teams = response.data.teams
      } catch (error) {
        console.error('Error fetching teams:', error)
      }
    },
    async fetchBranches() {
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        this.branches = response.data.branches
      } catch (error) {
        console.error('Error fetching bracnhes:', error)
      }
    },
    async fetchDepartments() {
      try {
        const response = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = response.data.departments
      } catch (error) {
        console.error('Error fetching departments:', error)
      }
    },
    async fetchDesignations() {
      try {
        const response = await this.$axios.get(axiosUrl.designations.getDesignations)
        this.designations = response.data.designations
      } catch (error) {
        console.error('Error fetching designations:', error)
      }
    },
    handleShowDialog(label) {
      if (this.selected.length === 0) {
        this.$toast.error('Please select at least one employee')
        return
      }
      this.dialogLable = label
      this.showDialog = !this.showDialog
      if (this.showDialog === false) {
        this.fetchEmployees(true)
        this.selected = []
        // this.selectedEmployeeIds.clear()
      }
    },
    getEmployeeTypes() {
      this.employeeTypes = employeeTypes.map((type) => ({ name: type }))
    },
    handleSelectedPlaceCurrent(place) {
      if (this.editEmp) {
        this.editEmp.addressLine1 = place.formatted_address
        this.selectedAddress = place.formatted_address
        this.editEmp.city = place.city
        this.editEmp.state = place.state
        this.editEmp.country = place.country
        this.editEmp.pinCode = place.pincode
      }
    },
    updateSelectedCount() {
      this.selectedCount = [
        this.selectedTeamName,
        this.selectedRoleFilter,
        this.selectedDepartment,
        this.selectedDesignation,
        this.selectedStatus
      ].filter((item) => item !== null && item !== '').length
    },
    // async toggleEmployeeStatus(employee) {
    //   try {
    //     employee.status = employee.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    //     await this.updateEmployeeDetails(employee)
    //     this.$toast.success('Employee status updated successfully!')
    //   } catch (error) {
    //     console.error('Error updating employee status:', error)
    //     this.$toast.error('Failed to update employee status.')
    //   }
    // },

    isFeatureVisible(feature) {
      return isModuleFeatureAllowed('EMPLOYEE_MANAGEMENT', feature)
    },

    // Add Employee Dialog Methods
    openAddEmployeeDialog() {
      if (this.orgLimit && this.totalEmployees >= this.orgLimit) {
        this.$toast.error('You have reached the maximum number of employees allowed for your plan.')
        return
      }
      this.showAddEmployeeDialog = true
    },

    closeAddEmployeeDialog() {
      this.showAddEmployeeDialog = false
    },

    // Assign Dialog Methods

    handleAssignOption(type) {
      // Close the old assign dialog
      // this.closeAssignDialog()
      // Directly handle the assignment
      this.handleShowDialog(type)
    },

    // handleAssignOption(type) {
    //   this.closeAssignDialog()
    //   this.handleShowDialog(type)
    // },

    showSelectionWarning() {
      this.$toast.warning('Please select at least one employee')
    },

    /**
     * Handle bulk status update from FloatingActionBar menu
     * @param {Object} menuItem - The selected menu item
     */
    handleBulkStatusUpdate(menuItem) {
      if (this.selected.length === 0) {
        this.$toast.warning('Please select at least one employee')
        return
      }

      // Call the bulk update method with the selected status
      this.bulkUpdateEmployeeStatus(menuItem.value)
    },

    /**
     * Bulk update employee status for selected employees
     * @param {String} status - The status to set ('ACTIVE' or 'INACTIVE')
     */
    async bulkUpdateEmployeeStatus(status) {
      try {
        // Show loading state
        this.bulkUpdating = true

        // Counter for successful updates
        let successCount = 0
        let errorCount = 0

        // Update each employee
        for (const employee of this.selected) {
          try {
            // Create a copy of the employee with updated status
            const updatedEmployee = { ...employee, status: status }

            // Call the existing update method
            await this.updateEmployeeDetails(updatedEmployee)
            successCount++
          } catch (error) {
            console.error(`Error updating employee ${employee._id}:`, error)
            errorCount++
          }
        }

        // Refresh the employee list
        await this.fetchEmployees(true)

        // Clear selection
        this.selected = []
        // this.selectedEmployeeIds.clear()

        // Show success message
        if (errorCount === 0) {
          this.$toast.success(
            `Successfully updated ${successCount} employee(s) to ${status} status!`
          )
        } else {
          this.$toast.success(
            `Successfully updated ${successCount} employee(s) to ${status} status. ${errorCount} failed.`
          )
        }
      } catch (error) {
        console.error('Error updating employee status:', error)
        this.$toast.error('Failed to update employee status.')
      } finally {
        // Reset loading state
        this.bulkUpdating = false
      }
    },

    async getOrgSalary(role) {
      try {
        const response = await this.$axios.get('/org/salary-templates')
        this.orgSalaries = response.data?.salaryTemplates
        // console.log({ s: this.orgSalaries })
        if (this.orgSalaries?.length > 0) {
          this.availableTemplates = this.orgSalaries.filter(
            (template) => template.role === role._id
          )
        }
        return this.orgSalaries
      } catch (error) {
        console.log(error)
      }
    },

    async fetchOrganization() {
      try {
        const response = await this.$axios.get('/organizations')
        if (response.data.success && response.data.organizations.length > 0) {
          const org = response.data.organizations[0]
          if (org.plan_limitations && org.plan_limitations.NUMBER_OF_USERS) {
            this.orgLimit = org.plan_limitations.NUMBER_OF_USERS
          }
        }
      } catch (error) {
        console.error('Error fetching organization:', error)
      }
    },

    handleAssignLocationClick() {
      if (this.selected.length === 0) {
        this.$toast.warning('Please select at least one employee')
        return
      }
      // Directly open the assign location dialog with both team and branch selections
      this.handleShowDialog(UpdateCaseDialogLabels.ASSIGN_LOCATION)
    },

    handleAssignDepartmentClick() {
      if (this.selected.length === 0) {
        this.$toast.warning('Please select at least one employee')
        return
      }
      // Directly open the assign department dialog with both department and designation selections
      this.handleShowDialog(UpdateCaseDialogLabels.ASSIGN_DEPARTMENT)
    }
  },
  async mounted() {
    const empQuery = this.$route.query?.search
    if (empQuery) {
      this.filterName = empQuery
    }
    if (this.$attrs.roles === undefined) {
      await this.fetchRoles()
    }
    await this.fetchManager()
    await this.getConfig()
    if (this.$attrs.teams === undefined) {
      await this.fetchTeams()
    }
    await this.getEmployeeTypes()
    if (this.$attrs.branches === undefined) {
      await this.fetchBranches()
    }
    if (this.$attrs.departments === undefined) {
      await this.fetchDepartments()
    }
    if (this.$attrs.designations === undefined) {
      await this.fetchDesignations()
    }
    await this.fetchOrganization()
    await this.fetchEmployees(true)
  }
}
</script>

<style scoped>
.filter-row {
  visibility: visible;
  max-height: 100px;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
}

.hidden {
  max-height: 0;
  visibility: hidden;
}

.fixed-width-column {
  width: 150px;
  font-size: 14px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.even-row {
  background-color: #e1dbdb;
  /* Your even row background color */
}

.odd-row {
  background-color: #ffffff;
  /* Your odd row background color */
}

.v-data-table >>> .v-data-table__checkbox {
  padding-left: 5px;
}

.warning-text {
  color: #fb8c00;
}

.custom-checkbox >>> .v-input--selection-controls__ripple {
  display: none;
}

.custom-checkbox:hover >>> .v-input--selection-controls__input .v-icon {
  color: #2a83ff !important;
}

.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}

.wfm-emp-card {
  box-shadow: none !important;
  border: 1px solid #cdced0 !important;
}

.wfm-search-fields.v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 40px !important;
}
.table-radius {
  border-radius: 0px !important;
}

.custom-expansion-panels >>> .v-expansion-panel {
  box-shadow: none !important;
}
.custom-expansion-panels >>> .v-expansion-panel-header {
  border: none;
}
.custom-expansion-panels >>> .v-expansion-panel-content {
  padding: 0;
}

.v-expansion-panel {
  box-shadow: none !important;
}

/* Remove shadow from the header as well */
.v-expansion-panel-header {
  box-shadow: none !important;
}

/* Remove border if needed */
.v-expansion-panel-header {
  border: none !important;
}

/* Ensure that content padding is set to your needs */
.v-expansion-panel-content {
  padding: 16px !important; /* Adjust padding as needed */
}

/* Optional: Ensure background color is consistent */
.v-expansion-panel-header,
.v-expansion-panel-content {
  background-color: #fff; /* Set background color if needed */
}
::v-deep .v-list-item__title {
  font-size: 14px !important;
}
.unassignedOptions {
  position: sticky;
  font-size: 14px !important;
  top: 0;
  z-index: 2;
  background-color: #ffffff;
}
.v-list {
  padding: 0px !important;
}
.text-green-500 {
  color: #4caf50;
}
.text-red-500 {
  color: #f44336;
}
.selected-card {
  border: 2px solid #2a83ff !important;
}
.highlighted {
  background: #21427d;
  color: white !important;
}

::v-deep .v-card {
  padding: 5px;
  border: none;
  box-shadow: none;
}

::v-deep .v-card {
  padding: 5px;
  border: none;
  box-shadow: none;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
